import React from "react";
import { WorkflowTemplate } from "../../types/workflow";
import { getIntegrationLabel } from "../../utils/integrationLabels";

interface WorkflowTemplateCardProps {
  template: WorkflowTemplate;
  onCreateWorkflow: (template: WorkflowTemplate) => void;
}

const WorkflowTemplateCard: React.FC<WorkflowTemplateCardProps> = ({
  template,
  onCreateWorkflow,
}) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "data_collection":
        return "📊";
      case "analysis":
        return "📈";
      case "notification":
        return "📧";
      case "integration":
        return "🔗";
      default:
        return "⚙️";
    }
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case "simple":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "complex":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatEstimatedTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const isNewTemplate = [
    "investor-weekly-update",
    "stakeholder-product-progress",
  ].includes(template.id);

  return (
    <div
      className={`bg-white rounded-lg shadow-md border transition-shadow duration-200 hover:shadow-lg flex flex-col h-full ${
        isNewTemplate
          ? "border-gray-300 ring-1 ring-gray-200"
          : "border-gray-200"
      }`}
    >
      {/* Header - Responsive padding */}
      <div className="p-3 pb-3 md:p-5 lg:p-6 lg:pb-4 flex-grow">
        {/* New Template Badge */}
        {/* {isNewTemplate && (
          <div className="mb-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              ✨ New Template
            </span>
          </div>
        )} */}

        {/* Title and Category Section */}
        <div className="mb-3">
          {/* Mobile: Stacked layout, Tablet+: Side-by-side */}
          <div className="flex flex-col space-y-2 md:flex-row md:items-start md:justify-between md:space-y-0">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <span className="text-xl md:text-2xl flex-shrink-0">
                {getCategoryIcon(template.category)}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 leading-tight">
                  {template.name}
                </h3>
                <p className="text-xs md:text-sm text-gray-500 capitalize">
                  {template.category.replace("_", " ")}
                </p>
              </div>
            </div>
          </div>
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full self-start ${getComplexityColor(
              template.complexity
            )}`}
          >
            {template.complexity}
          </span>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 line-clamp-3 mb-3 md:mb-4">
          {template.description}
        </p>

        {/* Integrations */}
        {template.requirements.integrations.length > 0 && (
          <div className="space-y-2 mb-3 md:mb-4">
            <span className="text-xs font-medium text-gray-500">
              Integrations:
            </span>
            <div className="flex flex-wrap gap-1">
              {template.requirements.integrations
                .slice(0, 3) // Show 3 on all screen sizes for consistency
                .map((integration) => (
                  <span
                    key={integration}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                  >
                    {getIntegrationLabel(integration)}
                  </span>
                ))}
              {template.requirements.integrations.length > 3 && (
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                  +{template.requirements.integrations.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer - Responsive layout */}
      <div className="px-4 py-4 md:px-5 lg:px-6 bg-gray-50 border-t border-gray-200 rounded-b-lg mt-auto">
        {/* Mobile: Button only, Tablet+: Time + Button */}
        <div className="flex flex-col space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
          {/* Time detail - hidden on mobile, shown on tablet+ */}
          <div className="hidden md:flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <span>⏱️</span>
              <span>
                ~{formatEstimatedTime(template.estimatedExecutionTime)}
              </span>
            </div>
          </div>

          {/* Button - full width on mobile, auto width on tablet+ */}
          <button
            onClick={() => onCreateWorkflow(template)}
            className="w-full md:w-auto px-4 py-3 md:py-2 bg-[#303030] text-white text-sm font-medium rounded-md hover:bg-[#1f1f1f] focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2 transition-colors"
          >
            Create Workflow
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTemplateCard;
